import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import fetcher, { FleetFile } from '@/libs/fleet-api/fetcher';
import {
	CreditAnalysisResultPayload,
	getCustomerCheckingInformation,
	getSlikRobotikDetail,
	getSlikRobotikResult,
	submitCreditAnalysisResult,
} from '@/libs/fleet-api/services/prospect';
import AnalisaSLIKFormRobotikCCDH from '@/pages/ccdh/customer-checking/AnalisaSLIKForm.robotik';
import NegativeModal, {
	useNegativeModal,
} from '@/pages/fbdh/customer-checking/NegativeModal';
import RejectModal, {
	useRejectModal,
} from '@/pages/fbdh/customer-checking/RejectModal';
import { FileSearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Alert,
	Col,
	Divider,
	Form,
	Input,
	Radio,
	Result,
	Row,
	Table,
	Tabs,
	Typography,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';

export type CustomerCheckingModalState = null | {
	prospectId: string;
};

type CustomerCheckingModalProps = {
	modal: CustomerCheckingModalState;
	onClose: () => void;
};

const FAQ_TITLE = {
	'dukcapil': 'DUKCAPIL',
	'dttot': 'Daftar Terduga Teroris dan Organisasi Teroris (DTTOT)',
	'dppspm': 'Daftar Pendanaan Proliferasi Senjata Pemusnah Massal (DPPSPM)',
	'online-betting': 'Daftar Pihak/Rekening Terkait Judi Online',
	'pep':
		'Analisa EDD (Enhanced Due Dilligent) High Risk & Politically Exposed Person (PEP)',
};

const MAP_ANALYSIS = {
	'dukcapil': 'dukcapilAnalysis',
	'dttot': 'dttotAnalysis',
	'dppspm': 'dppspmAnalysis',
	'online-betting': 'onlineBettingAnalysis',
	'pep': 'pepAnalysis',
};

const CATEGORY_LABEL = {
	'dukcapil': 'DUKCAPIL',
	'dttot': 'DTTOT',
	'dppspm': 'DPPSPM',
	'online-betting': 'Judi Online',
	'pep': 'PEP',
};

const CustomerCheckingModal: FC<CustomerCheckingModalProps> = ({
	modal,
	onClose,
}) => {
	const { navigate, notification } = useApp();
	const {
		openPreviewModal,
		openShareholderModal,
		openManagementModal,
		openRejectHistoryModal,
		openNegativeHistoryModal,
	} = useModals();

	const { data, isLoading } = useQuery({
		queryKey: ['getCustomerCheckingInformation', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, prospectId] = queryKey;
			return getCustomerCheckingInformation(prospectId ?? '');
		},
		enabled: !!modal?.prospectId,
	});

	const {
		mainInformation,
		managementSlikInformation,
		shareholderSlikInformation,
		creditExternalChecks,
		rejectedHistoryAnalysis,
		negativeHistoryAnalysis,
		approvalHistory,
		slikCheck,
		customerNegativeHistory,
		customerRejectionHistory,
		slikInquiryMetric,
	} = data?.data || {};

	const { form } = useForm<CreditAnalysisResultPayload>();

	const submitResult = useMutation({ mutationFn: submitCreditAnalysisResult });

	const [successModal, setSuccessModal] = useState(false);
	const [rejectModal, setRejectModal] = useRejectModal();
	const [negativeModal, setNegativeModal] = useNegativeModal();
	const [activeTab, setActiveTab] = useState<string>('Internal Checking');

	const isSlikRobotik = slikInquiryMetric?.isRobotic ?? false;
	const isSlikRobotikFailed = slikInquiryMetric?.isRoboticFailed ?? true;

	// get result slik robotik
	const { data: slikRobotikResult } = useQuery({
		queryKey: ['getSlikRobotikResult', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getSlikRobotikResult(id!);
		},
		enabled: isSlikRobotik && !!modal?.prospectId,
	});

	// get result detail robotik
	const { data: slikRobotikDetail } = useQuery({
		queryKey: ['getSlikRobotikDetail', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getSlikRobotikDetail(id!);
		},
		enabled: isSlikRobotik && !!modal?.prospectId,
	});

	const getAnalysisLabel = (category: string) => {
		return (
			CATEGORY_LABEL[category as keyof typeof CATEGORY_LABEL] ??
			category.toUpperCase()
		);
	};

	return (
		<Fragment>
			<FleetModal
				open={Boolean(modal)}
				width={940}
				cancelText="BACK"
				buttonPosition="end"
				closable={false}
				okButtonProps={{
					hidden: true,
				}}
				onCancel={() => onClose?.()}
			>
				<FleetTitle>Main Information</FleetTitle>
				<DataRow
					loading={isLoading}
					style={{
						marginBottom: 20,
					}}
					items={[
						[
							['Nama Customer', mainInformation?.customerName],
							['Prospect ID', mainInformation?.prospectId],
						],
						[
							['Kelas Industri', mainInformation?.industrySector.industryClass],
							['Sumber Order', mainInformation?.orderSource],
						],
						[
							[
								'Portofolio Guideline',
								mainInformation?.industrySector.industryClass,
							],
							[
								'SLIK Result',
								mainInformation?.slikResult
									? 'Kol ' + mainInformation?.slikResult
									: '-',
							],
						],
						[
							['FRM Name', mainInformation?.frm?.value ?? '-'],
							['CA Name', mainInformation?.creditAnalyst?.value ?? '-'],
						],
					]}
				/>

				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={async (values) => {
						try {
							setSuccessModal(false);
							const { data } = await submitResult.mutateAsync({
								prospectId: mainInformation!.prospectId,
								notes: values.notes,
								dppspmAnalysis: values.dppspmAnalysis,
								dttotAnalysis: values.dttotAnalysis,
								dukcapilAnalysis: values.dukcapilAnalysis,
								onlineBettingAnalysis: values.onlineBettingAnalysis,
								pepAnalysis: values.pepAnalysis,
								negativeHistoryAnalysis: values.negativeHistoryAnalysis,
								rejectedHistoryAnalysis: values.rejectedHistoryAnalysis,
								slikFile: values.slikFile,
								slikResult: values.slikResult,
								answers: values.answers.filter((answer) => answer),
								managementSlikInformation: values.managementSlikInformation,
								shareholderSlikInformation: values.shareholderSlikInformation,
								selectedCompanyIndexes: values.selectedCompanyIndexes,
								selectedShareholderIndexes: values.selectedShareholderIndexes,
								isSlikRobotik: values.isSlikRobotik,
							});
							if (data) {
								setSuccessModal(true);
							}
						} catch (error) {
							setSuccessModal(false);
							if (fetcher.isError(error)) {
								const message = await error.json();
								notification.error({
									duration: 20,
									message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
									description: (
										<div>
											{message?.metadata?.debugInfo?.messages?.length ? (
												<ul>
													{message?.metadata?.debugInfo?.messages.map(
														(msg: string) => <li key={msg}>{msg}</li>,
													)}
												</ul>
											) : (
												<pre>Opps, Something went wrong</pre>
											)}
										</div>
									),
								});
							}
						}
					}}
				>
					<Tabs
						activeKey={activeTab}
						onChange={(key) => setActiveTab(key)}
						items={[
							{
								label: 'Internal Checking',
								key: 'Internal Checking',
								children: (
									<Fragment>
										<FleetTitle>Reject Customer History</FleetTitle>
										<Table
											scroll={{ x: 'max-content' }}
											style={{
												marginBottom: 20,
											}}
											bordered
											loading={isLoading}
											pagination={false}
											dataSource={
												customerRejectionHistory?.map((reject, key) => {
													return {
														key,
														...reject,
													};
												}) ?? []
											}
											columns={[
												{
													title: 'No',
													render: (_, record) => record.no,
												},
												{
													title: 'Prospect ID',
													render: (_, record) => record.prospectId,
												},
												{
													title: 'Customer Name',
													render: (_, record) => record.customerName,
												},
												{
													title: 'Reject Status',
													align: 'center',
													render: (_, record) => record.rejectedStatus,
												},
												{
													title: 'Reject Date',
													align: 'center',
													render: (_, record) =>
														record.rejectedDate
															? dayjs(record.rejectedDate).format('DD/MM/YYYY')
															: '-',
												},
												{
													title: 'Rejected Process',
													align: 'center',
													render: (_, record) => record.rejectedProcess,
												},
												{
													title: 'Rejected By',
													align: 'center',
													render: (_, record) => record.rejectedBy,
												},
												{
													title: 'Detail Reject',
													align: 'center',
													render: (_, record) => (
														<a
															href="#"
															onClick={(e) => {
																e.preventDefault();
																openRejectHistoryModal(record.rejectedId);
															}}
														>
															<FileSearchOutlined />
														</a>
													),
												},
											]}
										/>

										<Row
											gutter={[40, 20]}
											style={{ marginBottom: 30 }}
										>
											<Col
												span={6}
												style={{ fontWeight: 600 }}
											>
												Analisa Reject History
											</Col>
											<Col span={18}>
												{rejectedHistoryAnalysis?.analysisResult ?? '-'}
											</Col>
										</Row>

										<FleetTitle>Negative Customer History</FleetTitle>
										<Table
											style={{
												marginBottom: 20,
											}}
											bordered
											scroll={{ x: 'max-content' }}
											loading={isLoading}
											pagination={false}
											dataSource={
												customerNegativeHistory?.map((negative, key) => {
													return {
														key,
														...negative,
													};
												}) ?? []
											}
											columns={[
												{
													title: 'No',
													render: (_, record) => record.no,
												},
												{
													title: 'Prospect ID',
													render: (_, record) => record.prospectId ?? '-',
												},
												{
													title: 'Customer Name',
													render: (_, record) => record.customerName,
												},
												{
													title: 'Negative Status',
													align: 'center',
													render: (_, record) =>
														record.negativeStatus
															? record.negativeStatus === 'Bad' ||
																record.negativeStatus === 'Warning'
																? 'Yes'
																: 'No'
															: '-',
												},
												{
													title: 'Type',
													align: 'center',
													render: (_, record) => (
														<span
															className={`negative-badge status-${record.negativeType?.toLowerCase()}`}
														>
															{record.negativeType ?? '-'}
														</span>
													),
												},
												{
													title: 'Negative Date',
													align: 'center',
													render: (_, record) =>
														record.negativeDate
															? dayjs(record.negativeDate).format('DD/MM/YYYY')
															: '-',
												},
												{
													title: 'Source',
													align: 'center',
													render: (_, record) => record.source ?? '-',
												},
												{
													title: 'Active',
													align: 'center',
													render: (_, record) =>
														record.isActive === 'Active' ? 'Yes' : 'No',
												},
												{
													title: 'Detail Negative',
													align: 'center',
													render: (_, record) => (
														<a
															href="#"
															onClick={(e) => {
																e.preventDefault();
																openNegativeHistoryModal(record);
																// setNegativeModal(true);
															}}
														>
															<FileSearchOutlined />
														</a>
													),
												},
											]}
										/>
										<Row
											gutter={[40, 20]}
											style={{ marginBottom: 30 }}
										>
											<Col
												span={6}
												style={{ fontWeight: 600 }}
											>
												Analisa Negative History
											</Col>
											<Col span={18}>
												{negativeHistoryAnalysis?.analysisResult ?? '-'}
											</Col>
										</Row>
										<Divider />

										<FleetTitle>View Data Checking SLIK</FleetTitle>
										<Row
											gutter={[40, 20]}
											style={{ marginBottom: 30 }}
										>
											<Col span={12}>
												<Row>
													<Col
														span={12}
														style={{ fontWeight: 600 }}
													>
														View Form FAPP
													</Col>
													<Col span={12}>
														{slikCheck?.slikFormFile?.id ? (
															<a
																onClick={(e) => {
																	e.preventDefault();
																	if (!slikCheck?.slikFormFile) return;
																	openPreviewModal(slikCheck?.slikFormFile);
																}}
															>
																Form FAPP
															</a>
														) : (
															<span>-</span>
														)}
													</Col>
												</Row>
											</Col>
											<Col span={12}>
												<Row>
													<Col
														span={12}
														style={{ fontWeight: 600 }}
													>
														View Form PDP
													</Col>
													<Col span={12}>
														{slikCheck?.applicationLetterFile?.id ? (
															<a
																onClick={(e) => {
																	e.preventDefault();
																	if (!slikCheck?.applicationLetterFile) return;
																	openPreviewModal(
																		slikCheck?.applicationLetterFile,
																	);
																}}
															>
																Form-PDP
															</a>
														) : (
															<span>-</span>
														)}
													</Col>
												</Row>
											</Col>
										</Row>
									</Fragment>
								),
							},
							{
								label: 'SLIK Checking',
								key: 'SLIK Checking',
								children: (
									<Fragment>
										<Typography.Title level={3}>
											SLIK Information
										</Typography.Title>
										{isSlikRobotik && !isSlikRobotikFailed ? (
											<Fragment>
												<Alert
													message="Prospect ini menggunakan SLIK Robotik"
													type="warning"
													showIcon
													className="custom-alert-info"
												/>
												<AnalisaSLIKFormRobotikCCDH
													shareholderSlikInformation={
														shareholderSlikInformation ?? []
													}
													managementSlikInformation={
														managementSlikInformation ?? []
													}
													data={slikRobotikDetail?.data ?? []}
													result={slikRobotikResult?.data.details}
												/>
											</Fragment>
										) : (
											<Fragment>
												<Row gutter={[40, 20]}>
													<Col span={12}>
														<Row>
															<Col
																span={12}
																style={{ fontWeight: 600 }}
															>
																View SLIK
															</Col>
															<Col span={12}>
																<a
																	onClick={(e) => {
																		e.preventDefault();
																		openPreviewModal(
																			slikCheck?.slikFormFile as FleetFile,
																		);
																	}}
																>
																	SLIK.pdf
																</a>
															</Col>
														</Row>
													</Col>
												</Row>
												<Tabs
													style={{
														marginBottom: 20,
														marginTop: 40,
													}}
													items={[
														{
															label: 'Pengurus',
															key: 'Pengurus',
															children: (
																<Table
																	scroll={{ x: 'max-content' }}
																	bordered
																	loading={isLoading}
																	pagination={false}
																	dataSource={
																		managementSlikInformation?.map(
																			(manager, key) => {
																				return {
																					key,
																					...manager,
																				};
																			},
																		) ?? []
																	}
																	columns={[
																		{
																			title: 'View',
																			width: 50,
																			align: 'center',
																			render: (_, record) => (
																				<a
																					onClick={(e) => {
																						e.preventDefault();
																						openManagementModal(record.id);
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			),
																		},
																		{
																			title: 'View Ideb',
																			width: 100,
																			align: 'center',
																			render: (_, record) => (
																				<a
																					onClick={(e) => {
																						e.preventDefault();
																						openPreviewModal(
																							record.idebFile as unknown as FleetFile,
																						);
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			),
																		},
																		{
																			title: 'Nama',
																			render: (_, record) => record.name,
																		},
																		{
																			title: 'No. ID',
																			render: (_, record) =>
																				record.identityNumber,
																		},
																	]}
																/>
															),
														},
														{
															label: 'Pemegang Saham',
															key: 'Pemegang Saham',
															children: (
																<Table
																	scroll={{ x: 'max-content' }}
																	bordered
																	loading={isLoading}
																	pagination={false}
																	dataSource={
																		shareholderSlikInformation?.map(
																			(shareholder, key) => {
																				return {
																					key,
																					...shareholder,
																				};
																			},
																		) ?? []
																	}
																	columns={[
																		{
																			title: 'View',
																			width: 50,
																			align: 'center',
																			render: (_, record) => (
																				<a
																					onClick={(e) => {
																						e.preventDefault();
																						openShareholderModal(record.id);
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			),
																		},
																		{
																			title: 'View Ideb',
																			width: 100,
																			align: 'center',
																			render: (_, record) => (
																				<a
																					onClick={(e) => {
																						e.preventDefault();
																						openPreviewModal(
																							record.idebFile as unknown as FleetFile,
																						);
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			),
																		},
																		{
																			title: 'Nama',
																			render: (_, record) => record.name,
																		},

																		{
																			title: 'No. ID',
																			render: (_, record) =>
																				record.identityNumber,
																		},
																	]}
																/>
															),
														},
													]}
												/>
											</Fragment>
										)}
									</Fragment>
								),
							},
							{
								label: 'Eksternal Checking',
								key: 'Eksternal Checking',
								children: (
									<div style={{ marginBottom: 20 }}>
										{creditExternalChecks?.map((check, groupIndex) => {
											return (
												<div
													key={groupIndex}
													style={{ marginBottom: 20 }}
												>
													<FleetTitle>
														{FAQ_TITLE[
															check.category as keyof typeof FAQ_TITLE
														] ?? check.category}
													</FleetTitle>
													{check.creditExternalQuestions.map(
														({ question, answer, evidenceFile }) => {
															if (question.id === 11) {
																if (!evidenceFile) return null;
																return (
																	<Fragment key={question.id}>
																		<Divider />
																		<Row gutter={[40, 20]}>
																			<Col span={18}>{question.value}</Col>
																			<Col
																				span={6}
																				style={{ textAlign: 'right' }}
																			>
																				<a
																					href="#"
																					className="trigger-popup"
																					onClick={(e) => {
																						e.preventDefault();
																						openPreviewModal(
																							evidenceFile as FleetFile,
																						);
																					}}
																				>
																					Evidence
																				</a>
																			</Col>
																		</Row>
																	</Fragment>
																);
															}
															return (
																<Fragment key={question.id}>
																	<Divider />
																	<Row gutter={[20, 20]}>
																		<Col span={18}>
																			<Form.Item
																				name={['answers', question.id, 'id']}
																				initialValue={question.id}
																				noStyle
																			/>
																			<Form.Item
																				name={[
																					'answers',
																					question.id,
																					'answer',
																				]}
																				initialValue={answer.value}
																				label={question.value}
																				labelCol={{
																					span: 16,
																					style: {
																						whiteSpace: 'normal',
																					},
																				}}
																			>
																				<Radio.Group
																					style={{ display: 'flex' }}
																				>
																					<Radio
																						value={'Ya'}
																						style={{ lineHeight: '1' }}
																						disabled
																					>
																						Ya
																					</Radio>
																					<Radio
																						value={'Tidak'}
																						disabled
																						style={{ lineHeight: '1' }}
																					>
																						Tidak
																					</Radio>
																				</Radio.Group>
																			</Form.Item>
																		</Col>
																		<Col
																			span={6}
																			style={{
																				display: 'flex',
																				justifyContent: 'flex-end',
																			}}
																		>
																			<a
																				href="#"
																				className="trigger-popup"
																				onClick={(e) => {
																					e.preventDefault();
																					openPreviewModal(
																						evidenceFile as FleetFile,
																					);
																				}}
																			>
																				Evidence
																			</a>
																		</Col>
																	</Row>
																</Fragment>
															);
														},
													)}
													<Divider />
													<Row gutter={[40, 20]}>
														<Col span={24}>
															<Form.Item
																// @ts-expect-error type error
																name={MAP_ANALYSIS[check.category]}
																label={`Analisa ${getAnalysisLabel(check.category)}`}
																labelCol={{
																	span: 6,
																	style: {
																		alignSelf: 'center',
																	},
																}}
																wrapperCol={{
																	style: {
																		marginLeft: -10,
																	},
																}}
																rules={[formRule.required()]}
																initialValue={check.analysisResult}
															>
																<Input.TextArea
																	rows={4}
																	disabled
																/>
															</Form.Item>
														</Col>
													</Row>
												</div>
											);
										})}
									</div>
								),
							},
						]}
						style={{
							marginBottom: 20,
						}}
					/>
					<FleetTitle>Approval History</FleetTitle>
					<Table
						scroll={{ x: 'max-content' }}
						bordered
						loading={isLoading}
						pagination={false}
						dataSource={
							approvalHistory?.map((approval, key) => {
								return {
									key,
									...approval,
								};
							}) ?? []
						}
						columns={[
							{
								title: 'No',
								render: (_, record) => record.no,
							},
							{
								title: 'Name',
								render: (_, record) => record.approvalBy,
							},
							{
								title: 'Date',
								render: (_, record) => record.approvalDate,
							},
							{
								title: 'Process',
								render: (_, record) => record.approvalProcess,
							},
							{
								title: 'Status',
								align: 'center',
								render: (_, record) => {
									return (
										<span
											className={`review-status ${record.approvalStatus.toLowerCase()}`}
										>
											{record.approvalStatus}
										</span>
									);
								},
							},
							{
								title: 'Approval Notes',
								render: (_, record) => record.approvalNote,
							},
						]}
						style={{ marginBottom: 20 }}
					/>
				</Form>
			</FleetModal>

			<FleetModal
				open={successModal}
				cancelText="BACK"
				okText="NEXT"
				onOk={() => {
					setSuccessModal(false);
					navigate('CCAAnalisaSLIKPage');
				}}
				onCancel={() => {
					setSuccessModal(false);
				}}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses</div>}
					// subTitle="Pengecekan customer berhasil disimpan. Silahkan hubungi FBDH jika tidak di proses lebih dari 3 hari"
				/>
			</FleetModal>

			<RejectModal
				modal={rejectModal}
				onClose={() => setRejectModal(false)}
			/>

			<NegativeModal
				modal={negativeModal}
				onClose={() => setNegativeModal(false)}
			/>
		</Fragment>
	);
};

export default CustomerCheckingModal;

export const useCustomerCheckingModal = () => {
	return useState<CustomerCheckingModalState>(null);
};
